<template>
  <v-container>
    <v-row>
      <v-col>
        <v-alert
          v-if="isEditing"
          type="warning"
          dense
          class="mb-4"
        >
          Modificando questa policy, cambierai i permessi per
          <strong>{{ affectedUsersCount }}</strong>
          utenti appartenenti ai gruppi associati.
        </v-alert>

        <v-card
          v-if="isEditing"
          elevation="12"
          rounded="lg"
          class="mb-4"
        >
          <v-toolbar
            flat
            dense
            color="color_green"
            dark
          >
            <v-toolbar-title>Assign Group</v-toolbar-title>
            <v-spacer />
            <v-btn
              icon
              :loading="groupsLoading"
              @click="loadGroups"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="selectedGroupId"
                  :items="fixedGroups"
                  label="Existing group"
                  clearable
                />
              </v-col>
            </v-row>

            <v-row class="mt-2">
              <v-col cols="12">
                <div class="text-subtitle-2 mb-2">Groups linked to this policy</div>
                <v-chip-group column>
                  <v-chip
                    v-for="g in groupsForPolicy"
                    :key="g.id"
                    class="mr-2 mb-2"
                    color="primary"
                    outlined
                  >
                    {{ g.label }}
                  </v-chip>
                  <div v-if="!groupsForPolicy.length" class="text-body-2">
                    No groups linked yet.
                  </div>
                </v-chip-group>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />

          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn
              text
              :disabled="groupsSaving"
              @click="resetGroupForm"
            >
              Reset
            </v-btn>
            <v-btn
              color="color_green"
              dark
              :loading="groupsSaving"
              :disabled="!canSaveGroup || groupsSaving"
              @click="saveGroupToPolicy"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>

        <create-api-policy
          :form="item_form"
          :disable-ws-card="false"
          @updateConfirmedPolicy="onPolicyCreated"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import * as api from '@/api/api.js';

export default {
  name: 'PolicyDetails',
  components: {
    CreateApiPolicy: () => import('@/components/policy/CreateApiPolicy')
  },
  data() {
    return {
      item_form: {
        policy_id: 0,
        policy_name: '',
        policy_description: '',
        quota_metric: 'calls',
        quota_period: 'monthly',
        quota_limit: null,
        max_calls_per_day: null
      },
      affectedUsersCount: 0,

      groupsLoading: false,
      groupsSaving: false,
      allGroups: [],
      selectedGroupId: null,
      fixedGroups: [
        'climate_sentinel',
        'DEV',
        'geobound',
        'timeseries',
        'WFS',
        'WMS'
      ]
    };
  },
  computed: {
    ...mapState(['policy']),
    isEditing() {
      return this.$route.params.policy_id !== undefined;
    },
    groupsForPolicy() {
      const pid = Number(this.item_form.policy_id);
      // Lista gruppi statica lato frontend: filtra solo i gruppi noti (fixedGroups)
      // e mostra quelli che risultano linkati alla policy nel DB.

      return (this.allGroups || [])
        .filter(g => Number(g.policy_id) === pid)
        .filter(g => this.fixedGroups.includes(g.id))
        .map(g => ({ id: g.id, label: g.id }));
    },
    canSaveGroup() {
      return !!this.selectedGroupId;
    }
  },
  async created() {
    // Imposta subito la modalità (serve a CreateApiPolicy in base a `policy.isModifingPolicy`).
    if (this.isEditing) {
      this.storeIsModifingPolicy(true);
    } else {
      // Nuova policy
      this.storeIsModifingPolicy(false);
      this.item_form = JSON.parse(JSON.stringify(this.policy.blankPolicy));
      this.item_form.policy_id = 0;
      this.item_form.policy_name = '';
      this.item_form.policy_description = '';
      this.storeCustomerCRMID(this.item_form);
    }

    // Carica elenco policies per trovare quella richiesta (edit).
    await this.storePolicies();

    if (this.isEditing) {
      const policyId = Number(this.$route.params.policy_id);
      const targetPolicy = this.policy.policies.find(
        p => Number(p.policy_id) === policyId
      );

      if (targetPolicy) {
        this.item_form = JSON.parse(JSON.stringify(targetPolicy));
        this.storeCustomerCRMID(this.item_form);
      }

      await this.loadGroups();

      try {
        const resp = await api.getAffectedUsersCount(this.item_form.policy_id);
        this.affectedUsersCount =
          resp?.data?.getAffectedUsersCount ??
          resp?.data?.affectedUsersCount ??
          0;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('getAffectedUsersCount failed:', e);
        this.affectedUsersCount = 0;
      }
    }
  },
  methods: {
    ...mapActions([
      'storePolicies',
      'storeIsModifingPolicy',
      'storeCustomerCRMID'
    ]),

    resetGroupForm() {
      this.selectedGroupId = null;
    },

    async loadGroups() {
      this.groupsLoading = true;

      try {
        const res = await api.getGroups();
        const rawGroups = res?.data?.listGroups ?? [];

        // Backend può ritornare anche group_name, ma la label resta statica (id).
        this.allGroups = rawGroups.map(g => ({
          id: g.group_id ?? g.id,
          policy_id: g.policy_id
        }));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('loadGroups failed:', e);
        this.allGroups = [];
      } finally {
        this.groupsLoading = false;
      }
    },

    async saveGroupToPolicy() {
      const groupId = this.selectedGroupId;

      if (!groupId) {
        return;
      }

      this.groupsSaving = true;

      try {
        await api.upsertGroup(groupId, Number(this.item_form.policy_id));
        await this.loadGroups();
        this.resetGroupForm();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('saveGroupToPolicy failed:', e);
      } finally {
        this.groupsSaving = false;
      }
    },

    onPolicyCreated() {
      // Dopo la creazione la mutation aggiorna `policy.blankPolicy`.
      // Lo usiamo per sincronizzare `item_form` e abilitare la parte workspace.
      this.item_form = JSON.parse(JSON.stringify(this.policy.blankPolicy));
      this.storeCustomerCRMID(this.item_form);
    }
  }
};
</script>

